import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { PUBLIC_PAGES_COPY } from "copy/publicPages";
import { useCopy } from "i18n/useCopy";

interface IAuthCallbackError {
  code: string | null;
  description: string | null;
}

function parseAuthCallbackError(search: string, hash: string): IAuthCallbackError {
  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

  const codeFromSearch = searchParams.get("error");
  const descriptionFromSearch = searchParams.get("error_description");
  if (codeFromSearch || descriptionFromSearch) {
    return {
      code: codeFromSearch,
      description: descriptionFromSearch,
    };
  }

  const codeFromHash = hashParams.get("error");
  const descriptionFromHash = hashParams.get("error_description");
  return {
    code: codeFromHash,
    description: descriptionFromHash,
  };
}

const CallbackPage = (): React.ReactElement => {
  const copy = useCopy();
  const location = useLocation();
  const callbackError = useMemo(
    () => parseAuthCallbackError(location.search, location.hash),
    [location.hash, location.search]
  );
  const hasAuthCallbackError = callbackError.code !== null || callbackError.description !== null;

  if (!hasAuthCallbackError) {
    return <div className="page-layout__content" />;
  }

  return (
    <div className="page-layout__content px-4 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-2xl rounded-lg border border-red-500/30 bg-red-500/10 p-4 sm:p-6">
        <h1 className="mb-2 text-xl font-semibold text-red-300 sm:text-2xl">
          {copy.text(PUBLIC_PAGES_COPY.authCallback.title)}
        </h1>
        <p className="mb-4 text-sm text-red-100 sm:text-base">
          {copy.text(PUBLIC_PAGES_COPY.authCallback.description)}
        </p>

        <div className="mb-4 rounded-md bg-black/20 p-3 text-xs text-red-100 sm:text-sm">
          {callbackError.code && (
            <p className="mb-1">
              <strong>{copy.text(PUBLIC_PAGES_COPY.authCallback.errorCodeLabel)}</strong> {callbackError.code}
            </p>
          )}
          {callbackError.description && (
            <p>
              <strong>{copy.text(PUBLIC_PAGES_COPY.authCallback.detailsLabel)}</strong> {callbackError.description}
            </p>
          )}
        </div>

        <div className="mb-4 text-sm text-red-100">
          <p className="mb-1">{copy.text(PUBLIC_PAGES_COPY.authCallback.suggestionsHeading)}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>{copy.text(PUBLIC_PAGES_COPY.authCallback.suggestions.retryLater)}</li>
            <li>{copy.text(PUBLIC_PAGES_COPY.authCallback.suggestions.switchNetwork)}</li>
            <li>{copy.text(PUBLIC_PAGES_COPY.authCallback.suggestions.contactSupport)}</li>
          </ul>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Link
            className="inline-flex items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
            to="/muggleTestV0"
          >
            {copy.text(PUBLIC_PAGES_COPY.authCallback.backToHome)}
          </Link>
          <Link
            className="inline-flex items-center justify-center rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-100 hover:bg-red-500/10"
            to="/muggleTestV0/support"
          >
            {copy.text(PUBLIC_PAGES_COPY.authCallback.openSupport)}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CallbackPage;
